import type { DashboardOrderEvent } from '../types/dashboard.types'

/**
 * Maps an order event to a human-readable Norwegian sentence.
 *
 * Uses sender_name + event_type + metadata to construct natural language.
 */
export function formatEventHumanReadable(event: DashboardOrderEvent): string {
  const sender = event.sender_name || 'Noen'
  const metadata = (event.metadata || {}) as Record<string, unknown>
  const type = event.event_type

  switch (type) {
    // --- Status ---
    case 'status_changed': {
      const newStatus = metadata.new_status as string | undefined
      if (newStatus === 'pending') return `${sender} klargjorde ordren`
      if (newStatus === 'delivered') return 'Ordren ble levert til kunde'
      if (newStatus === 'partial') return 'Ordren venter på deler'
      if (newStatus === 'import') return 'Ordren ble importert'
      if (newStatus === 'cancelled') return 'Ordren ble kansellert'
      if (newStatus === 'archived') return 'Ordren ble arkivert'
      return `${sender} endret status til "${newStatus ?? 'ukjent'}"`
    }
    case 'status_change': {
      const newStatus = metadata.new_status as string | undefined
      if (newStatus) return `Status endret til "${newStatus}"`
      return 'Status endret'
    }

    // --- Order lifecycle ---
    case 'order_created': {
      const customer = metadata.customer_name as string | undefined
      if (customer) return `Ny ordre opprettet til ${customer}`
      return `${sender} opprettet ordren`
    }
    case 'order_prepared': {
      const location = metadata.location as string | undefined
      if (location) return `${sender} klargjorde ordren på ${location}`
      return `${sender} klargjorde ordren`
    }
    case 'order_delivered':
      return 'Ordren ble levert til kunde'
    case 'delivery_confirmed':
      return 'Kunde bekreftet utlevering'
    case 'order_split':
      return 'Ordren ble delt opp'
    case 'order_edited':
      return `${sender} redigerte ordren`
    case 'order_updated': {
      const fields = metadata.changed_fields as Record<string, { label: string }> | undefined
      if (fields) {
        const labels = Object.values(fields).map((f) => f.label?.toLowerCase()).filter(Boolean)
        if (labels.length > 0) return `${sender} oppdaterte ${labels.join(', ')}`
      }
      return `${sender} oppdaterte ordren`
    }

    // --- Lines ---
    case 'lines_added': {
      const count = metadata.lines_added as number | undefined
      if (count && count > 0) {
        return `${count} ${count === 1 ? 'ny artikkel' : 'nye artikler'} lagt til`
      }
      return 'Nye artikler lagt til'
    }
    case 'lines_removed': {
      const count = metadata.lines_removed as number | undefined
      if (count && count > 0) {
        return `${count} ${count === 1 ? 'artikkel' : 'artikler'} fjernet`
      }
      return 'Artikler fjernet'
    }

    // --- Purchase orders ---
    case 'purchase_order_received': {
      const supplier = metadata.supplier_name as string | undefined
      if (supplier) return `Deler mottatt fra ${supplier}`
      return 'Deler mottatt'
    }
    case 'purchase_order_sent': {
      const supplier = metadata.supplier_name as string | undefined
      if (supplier) return `Bestilling sendt til ${supplier}`
      return 'Bestilling sendt til leverandør'
    }
    case 'purchase_order_created': {
      const supplier = metadata.supplier_name as string | undefined
      if (supplier) return `Bestilling opprettet til ${supplier}`
      return 'Bestilling opprettet'
    }
    case 'purchase_order_partial': {
      const supplier = metadata.supplier_name as string | undefined
      if (supplier) return `Dellevering mottatt fra ${supplier}`
      return 'Dellevering mottatt'
    }
    case 'purchase_order_email_sent':
      return 'Bestilling sendt per e-post'

    // --- Communication ---
    case 'sms_sent': {
      const recipient = metadata.recipient as string | undefined
      if (recipient) return `SMS sendt til ${recipient}`
      return 'SMS sendt til kunde'
    }
    case 'email_sent':
      return 'E-post sendt til kunde'

    // --- Field changes ---
    case 'delivery_date_changed': {
      const newDate = metadata.new_date as string | undefined
      if (newDate) {
        const formatted = formatNorwegianDate(newDate)
        return `Leveringsdato endret til ${formatted}`
      }
      return `${sender} endret leveringsdato`
    }
    case 'info_changed':
      return `${sender} oppdaterte notatet`
    case 'reference_changed': {
      const newVal = metadata.new_value as string | undefined
      if (newVal) return `Referanse endret til "${newVal}"`
      return `${sender} endret referanse`
    }
    case 'your_ref_changed': {
      const newVal = metadata.new_value as string | undefined
      if (newVal) return `Kundens ref endret til "${newVal}"`
      return `${sender} endret kundens referanse`
    }
    case 'shipping_changed': {
      const newVal = metadata.new_value as string | undefined
      if (newVal) return `Frakt endret til "${newVal}"`
      return `${sender} endret fraktmetode`
    }

    // --- ERP ---
    case 'erp_sync':
      return 'ERP-oppdatering'
    case 'erp_created':
      return 'Opprettet i ERP'

    // --- Fallback ---
    default:
      return event.content || event.event_type
  }
}

function formatNorwegianDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr
    return date.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
    })
  } catch {
    return dateStr
  }
}
